/**
 * Central registry of all document types in the Documents Hub.
 *
 * The data lives in `documentTypes.json` — array order is display order —
 * because it is ALSO the source of the manifest the iOS Docs tab reads
 * (`/app-config/document-types.json`, written by
 * `scripts/write-document-type-manifest.js` before every start/build). A type
 * added or re-pointed there reaches the app with a hosting deploy. See
 * claude-docs/2026-09-14-documents-on-web-and-server-renders.md, Part 1.
 *
 * Per-entry notes:
 * - `ios.nativeId` is the iOS `DocumentType` raw value of a compiled native
 *   creator; `ios.creator` says whether iOS opens that ("native") or this web
 *   creator in its documents WebView ("web"). The proposal has no native
 *   creator, so it is "web".
 * - The proposal's colour is the brand accent. It was the page's own olive,
 *   which drew the list heading and the card icon invisibly on the Documents
 *   page. Deleting a proposal turns its online link off first (DocumentsHub),
 *   hence its `deleteConfirm`.
 */
import documentTypes from './documentTypes.json';

export const DOCUMENT_TYPES = Object.fromEntries(
  documentTypes.map((type) => [type.key, type])
);

/** Ordered array of document type keys for display */
export const DOCUMENT_TYPE_ORDER = documentTypes.map((type) => type.key);

/** Map from docType key to creator route segment */
export const CREATOR_ROUTES = Object.fromEntries(
  documentTypes.map((type) => [type.key, type.route])
);

/** Reverse map: route segment → docType key */
export const ROUTE_TO_DOC_TYPE = Object.fromEntries(
  Object.entries(CREATOR_ROUTES).map(([key, route]) => [route, key])
);

/**
 * The file a document entry opens: its PDF, else its image (cover
 * renderings), else a room package's combined PDF. iOS applies the same rule.
 */
export function getDocumentUrl(documentEntry) {
  return (
    documentEntry?.url ||
    documentEntry?.compositionUrl ||
    documentEntry?.combinedPdfUrl ||
    null
  );
}

/**
 * Get the document count for a given type from project data
 */
export function getDocumentCount(projectData, docTypeKey) {
  if (!projectData) return 0;
  const config = DOCUMENT_TYPES[docTypeKey];
  if (!config) return 0;
  const docs = projectData[config.firestoreKey];
  return Array.isArray(docs) ? docs.length : 0;
}

/**
 * Get the documents array for a given type from project data, sorted
 * newest-first by `createdAt` (ISO string). Documents missing
 * `createdAt` sort to the bottom so they're still visible.
 */
export function getDocuments(projectData, docTypeKey) {
  if (!projectData) return [];
  const config = DOCUMENT_TYPES[docTypeKey];
  if (!config) return [];
  const docs = projectData[config.firestoreKey];
  if (!Array.isArray(docs)) return [];
  return [...docs].sort((a, b) => {
    const aKey = a?.createdAt || '';
    const bKey = b?.createdAt || '';
    if (aKey === bKey) return 0;
    if (!aKey) return 1;
    if (!bKey) return -1;
    return bKey.localeCompare(aKey);
  });
}

/**
 * The documents a wizard ticked, in the order they were made (the stored
 * order). `listedIndices` are positions in `getDocuments`' list — newest
 * first, the list the checkboxes are drawn from. Indexing the STORED array
 * (oldest first) with them composed a different package from the one ticked.
 */
export function pickListedDocuments(projectData, docTypeKey, listedIndices) {
  const listed = getDocuments(projectData, docTypeKey);
  const chosen = new Set(listedIndices.map((i) => listed[i]).filter(Boolean));
  const stored = projectData?.[DOCUMENT_TYPES[docTypeKey]?.firestoreKey];
  return Array.isArray(stored) ? stored.filter((d) => chosen.has(d)) : [];
}
